import { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import GameHeader from '../components/game/GameHeader';
import SquaresGrid from '../components/game/SquaresGrid';
import ReservePanel from '../components/game/ReservePanel';
import FooterAdvert, { FOOTER_ADVERT_RESERVED_SPACE } from '../components/adverts/FooterAdvert';
import SquareSubmittedModal from '../components/modals/SquareSubmittedModal';
import SquareResultsModal from '../components/modals/SquareResultsModal';
import ConfirmReserveModal from '../components/modals/ConfirmReserveModal';
import SeatReservedModal from '../components/modals/SeatReservedModal';
import RulesView, { RULES_POPUP_PANEL_STYLE } from './RulesView';
import { useGetSquareQuery } from '../api/squares/getSquares';
import { useGetMySelectionQuery, type MySquaresSelection } from '../api/squares/getMySelection';
import {
  useGetSquaresOptionsQuery,
  type GetSquaresOptionsResponse,
} from '../api/squares/getSquaresOptions';
import { useSubmitSelectionMutation } from '../api/squares/submitSelection';
import { useReserveContestMutation, isAlreadyEnteredError } from '../api/squares/reserveContest';
import { upsertContestInMyPicksCache } from '../api/squares/myPicksCache';
import { useGetWinningSquaresQuery } from '../api/squares/getWinningSquares';
import { useGetFixtureScoresBatchQuery } from '../api/squares/getFixtureScores';
import { getTeamCode } from '../utils/teamCodes';
import { formatPrizePool } from '../utils/formatPrizePool';
import { DESKTOP_COLUMN_FIXED, MATCHUP_BAND } from '../utils/desktopColumn';
import { appendLobbyParams, isFromLobby, navigateToLobby } from '../utils/lobbyNavigation';

/**
 * Play View
 * Main game view with 10x10 squares grid
 */
export default function PlayView() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const contestId = searchParams.get('contestId');
  const mode = searchParams.get('mode'); // 'results' or null
  const isResultsMode = mode === 'results';

  // Fetch contest data (only if contestId is provided)
  const {
    data: contest,
    isLoading: contestLoading,
    error: contestError,
  } = useGetSquareQuery(contestId || null);

  // Always fetch user's selection (so they can view/edit before game starts)
  const {
    data: selectionData,
    isLoading: selectionLoading,
    refetch: refetchSelection,
  } = useGetMySelectionQuery(contestId || null);

  // Fetch options to map square numbers to option IDs for submission
  const questionId = contest?.question_id;
  const {
    data: optionsData,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useGetSquaresOptionsQuery(questionId || null);

  // Type the options data for use throughout the component
  const typedOptionsData = optionsData as GetSquaresOptionsResponse | undefined;

  if (optionsError) {
    console.error('Error loading options data:', optionsError);
  }

  // Fetch winning squares if game has started or is completed
  const contestStatus = contest?.status?.toUpperCase();
  const entriesLocked = contestStatus === 'ACTIVE' || contestStatus === 'COMPLETE';
  // When entries are locked (ACTIVE/COMPLETE), show View Results CTA — never Edit Entry
  const effectiveResultsMode = isResultsMode || entriesLocked;
  const shouldFetchWinners =
    contestStatus === 'ACTIVE' || contestStatus === 'COMPLETE' || isResultsMode;
  const { data: winningSquaresData } = useGetWinningSquaresQuery(
    shouldFetchWinners ? contestId || null : null
  );

  // Fetch fixture scores if game is live/active/completed
  const shouldFetchFixtureScores =
    contestStatus === 'ACTIVE' || contestStatus === 'COMPLETE' || isResultsMode;
  const contestIdsForFixtureScores = shouldFetchFixtureScores && contestId ? [contestId] : [];
  const { data: fixtureScoresBatch } = useGetFixtureScoresBatchQuery(contestIdsForFixtureScores);
  const fixture = contestId ? fixtureScoresBatch?.fixtures?.[contestId] : null;

  // Submit selection mutation
  const submitMutation = useSubmitSelectionMutation();
  // Reserve a Seat mutation (DKPLAT-529)
  const reserveMutation = useReserveContestMutation();

  // Reserve a Seat (DKPLAT-529): during the reserve window — contest open but
  // squares not posted yet (now < questions_posted_at) — show a reserve CTA
  // instead of the grid. Toggle off or no posted-at time → normal grid flow.
  // `has_reservation` comes from the backend's per-viewer overlay on
  // getSquaresContest (never cached, see square.controller.ts).
  const questionsPostedMs = contest?.questions_posted_at
    ? new Date(contest.questions_posted_at).getTime()
    : null;
  const inReserveWindow =
    contest?.reserve_a_seat_enabled === true &&
    contestStatus === 'PENDING' &&
    questionsPostedMs != null &&
    Date.now() < questionsPostedMs;
  const hasReservation = !!contest?.has_reservation;
  const showReservePanel = inReserveWindow && !isResultsMode;

  // Reserve confirmation flow (DKPLAT-529) — mirrors Pick'em's ReserveEntryScreen:
  // CTA opens a confirm modal, a successful POST swaps it for the "You're In"
  // confirmation carrying the reservation id.
  const [showConfirmReserveModal, setShowConfirmReserveModal] = useState(false);
  const [showSeatReservedModal, setShowSeatReservedModal] = useState(false);
  const [reservationId, setReservationId] = useState<string | null>(null);

  const handleReserve = () => {
    if (!contest?.pickem_id) return;
    setShowConfirmReserveModal(true);
  };

  const handleConfirmReserve = async () => {
    if (!contest?.pickem_id) return;
    try {
      const reservation = await reserveMutation.mutateAsync(contest.pickem_id);
      setReservationId(reservation?.id ?? null);
      setShowConfirmReserveModal(false);
      setShowSeatReservedModal(true);
    } catch (error) {
      console.error('Failed to reserve seat:', error);
      // Error toast is handled by the mutation. Keep the confirm modal open so the
      // user can retry — except on 409, where an entry already exists and retrying
      // can only fail again; the mutation refreshes the contest so the panel settles
      // into its true state behind the closing modal.
      if (isAlreadyEnteredError(error)) {
        setShowConfirmReserveModal(false);
      }
    }
  };

  // Selected squares state
  const [selectedSquares, setSelectedSquares] = useState<number[]>([]);
  // Modal state
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  // Track if we've initialized from API to prevent overriding user's active selection
  const hasInitializedFromApi = useRef(false);
  const lastSubmittedSquare = useRef<number | null>(null);

  // Reset initialization flag when contestId changes
  useEffect(() => {
    hasInitializedFromApi.current = false;
    lastSubmittedSquare.current = null;
    setSelectedSquares([]);
  }, [contestId]);

  // If returning from HelpView for rules popup flow, reopen rules popup once.
  useEffect(() => {
    const navState = location.state as { openRulesPopup?: boolean } | null;
    if (!navState?.openRulesPopup) return;

    setShowInfoPopup(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  // Map contest data to game data structure
  const gameData = useMemo(() => {
    if (!contest) {
      return null;
    }

    // Check if game has started
    const startTime = contest.start_time_utc ? new Date(contest.start_time_utc) : null;
    const now = new Date();
    const isGameStarted = startTime && now >= startTime;
    const contestStatus = contest.status?.toUpperCase();
    const isActive = contestStatus === 'ACTIVE';
    const isCompleted = contestStatus === 'COMPLETE' || contest.status === 'final';

    // Selections are allowed only when status is PENDING and game hasn't started.
    // Selections are LOCKED when:
    // - Status is ACTIVE (entries locked when contest goes live), OR
    // - Status is COMPLETE/final, OR
    // - Game has started and status is ACTIVE, OR
    // - In results mode
    const selectionsLocked = isResultsMode || isCompleted || isActive;

    // Numbers should only be revealed if:
    // 1. Game has started (start_time_utc has passed) AND status is ACTIVE/COMPLETE, OR
    // 2. Contest status is COMPLETE/final (game finished), OR
    // 3. In results mode
    const shouldRevealNumbers =
      (isGameStarted && (isActive || isCompleted)) || isCompleted || isResultsMode;

    // Get user's selection from API (always show if exists, even before game starts)
    // API client unwraps response.data, so selectionData is the response directly
    const typedSelectionData = selectionData as MySquaresSelection | undefined;
    const userSelectedSquare = typedSelectionData?.selection?.square_number || undefined;

    // Get numbers from selection data if available, otherwise from contest, or from optionsData
    // Only reveal if game has started and status allows
    const optionsDataTyped = optionsData as GetSquaresOptionsResponse | undefined;
    const rowNumbers = shouldRevealNumbers
      ? typedSelectionData?.contest?.row_digits ||
        contest.row_digits ||
        optionsDataTyped?.row_digits ||
        undefined
      : undefined;
    const colNumbers = shouldRevealNumbers
      ? typedSelectionData?.contest?.col_digits ||
        contest.col_digits ||
        optionsDataTyped?.col_digits ||
        undefined
      : undefined;

    // Get team names (Row Team = Team 1, Column Team = Team 2)
    const team1Name = contest.row_team_display_name || 'Team 1';
    const team2Name = contest.col_team_display_name || 'Team 2';

    // Use feed abbreviations (UF, SCAR) when available, else derive from display name
    const team1Abbreviation = contest.row_team_abbreviation?.trim() || getTeamCode(team1Name);
    const team2Abbreviation = contest.col_team_abbreviation?.trim() || getTeamCode(team2Name);

    // Get team colors (default to placeholder colors if not set)
    const team1Color = contest.row_team_bg_color || '#003087';
    const team2Color = contest.col_team_bg_color || '#CC0033';

    // Format prize pool
    const prizePool = formatPrizePool(contest.total_prize_amount);

    // Get winning square numbers from graded results
    const winningSquareNumbers = winningSquaresData?.currentWinningSquareNumbers || [];

    // Build squares array with winner flags
    // We need to map winning square numbers to grid positions
    // Square number formula: squareNumber = (rowIndex * 10) + colIndex + 1
    // So: rowIndex = Math.floor((squareNumber - 1) / 10), colIndex = (squareNumber - 1) % 10
    const squares = Array.from({ length: 100 }, (_, i) => {
      const squareNumber = i + 1; // Square numbers are 1-100
      const isWinner = winningSquareNumbers.includes(squareNumber);

      // Calculate which row/col index this square corresponds to
      const rowIndex = Math.floor(i / 10);
      const colIndex = i % 10;

      return {
        id: i,
        row: rowIndex,
        col: colIndex,
        isWinner,
      };
    });

    return {
      team1: {
        name: team1Name,
        abbreviation: team1Abbreviation,
        primaryColor: team1Color,
        secondaryColor: '#ffffff',
        jerseyUrl: contest.row_team_icon_url,
      },
      team2: {
        name: team2Name,
        abbreviation: team2Abbreviation,
        primaryColor: team2Color,
        secondaryColor: '#ffffff',
        jerseyUrl: contest.col_team_icon_url,
      },
      prizePool,
      contestName: contest.contest_name_en || contest.contest_name_fr || 'SQUARES',
      selectableSquares: contest.max_squares_per_user || 1,
      isLocked: selectionsLocked, // Locked when game started, completed, or in results mode (but NOT when PENDING or ACTIVE before game starts)
      numbersAssigned: shouldRevealNumbers, // Numbers revealed when game started
      userSelectedSquare, // User's selected square from API
      rowNumbers, // Row numbers (0-9 shuffled) - only revealed when game started
      colNumbers, // Column numbers (0-9 shuffled) - only revealed when game started
      squares, // Squares array with winner flags
      isGameLive: isGameStarted && (isActive || isCompleted), // Game is live when started and active/completed
    };
  }, [contest, isResultsMode, selectionData, winningSquaresData, optionsData]);

  // Map fixture scores to team scores
  const liveFixtureData = useMemo(() => {
    if (!fixture || !contest) return undefined;

    const rowTeamId = contest.row_team_id;
    const colTeamId = contest.col_team_id;

    // Determine which team is home/away and map scores
    let team1Score: number | null = null;
    let team2Score: number | null = null;

    if (fixture.homeTeam && fixture.awayTeam) {
      // Match team IDs to determine score mapping
      if (rowTeamId && fixture.homeTeam.id === rowTeamId) {
        team1Score = fixture.homeScore;
        team2Score = fixture.awayScore;
      } else if (rowTeamId && fixture.awayTeam.id === rowTeamId) {
        team1Score = fixture.awayScore;
        team2Score = fixture.homeScore;
      } else if (colTeamId && fixture.homeTeam.id === colTeamId) {
        team1Score = fixture.awayScore;
        team2Score = fixture.homeScore;
      } else if (colTeamId && fixture.awayTeam.id === colTeamId) {
        team1Score = fixture.homeScore;
        team2Score = fixture.awayScore;
      }
    }

    // Parse quarter and clock from fixture
    // Use fixture.quarter (1-4 from feed) when available; otherwise parse from clock string
    let quarter: string | undefined;
    let clock: string | undefined;

    // Prefer feed quarter number when available (NFL/NCAA use 1-2 → H1/H2, NBA uses 1-4 → Q1-Q4)
    const feedQuarter = fixture.quarter != null ? Number(fixture.quarter) : null;
    if (feedQuarter != null && feedQuarter >= 1 && feedQuarter <= 4) {
      quarter = `Q${feedQuarter}`; // GameHeader: NCAA maps Q1/Q2→H1/H2; NBA shows Q1–Q4
    }

    if (fixture.clock) {
      const clockStr = fixture.clock.trim();
      // NCAA: H1, H2, HT; NBA: Q1–Q4, FT - parse from clock if quarter not from feed
      if (!quarter) {
        const quarterMatch =
          clockStr.match(/Q([1-4]|FT|FINAL)/i) || clockStr.match(/\b(H[12]|HT)\b/i);
        if (quarterMatch) {
          const q = quarterMatch[1].toUpperCase();
          if (q === 'FINAL' || q === 'FT') {
            quarter = 'FT';
          } else if (q === 'H1' || q === 'H2' || q === 'HT') {
            quarter = q === 'H1' ? 'Q1' : q === 'H2' ? 'Q2' : q; // normalize to Q1/Q2 for display mapping
          } else {
            quarter = `Q${q}`;
          }
        }
      }
      const timeMatch = clockStr.match(/(\d+:\d+)/);
      if (timeMatch) {
        clock = timeMatch[1];
      } else if (!/Q[1-4]|FT|FINAL|H[12]|HT/i.test(clockStr)) {
        clock = clockStr;
      }
    }

    // If finished, set quarter to FT
    if (fixture.status === 'finished' && !quarter) {
      quarter = 'FT';
    }

    return {
      team1Score,
      team2Score,
      quarter,
      clock,
      status: fixture.status,
    };
  }, [fixture, contest]);

  // Initialize selected square from API (for both results mode and when user has existing selection)
  useEffect(() => {
    const typedSelectionData = selectionData as MySquaresSelection | undefined;
    const userSelectedSquareNumber = typedSelectionData?.selection?.square_number;

    // Convert square_number (1-100) to square.id (0-99)
    const userSelectedSquareId =
      userSelectedSquareNumber !== undefined && userSelectedSquareNumber !== null
        ? userSelectedSquareNumber - 1
        : null;

    // In results mode, always sync with API (selection is locked)
    if (isResultsMode) {
      if (userSelectedSquareId !== null) {
        setSelectedSquares([userSelectedSquareId]);
      } else {
        setSelectedSquares([]);
      }
      return;
    }

    // For play mode:
    // 1. Initial load: sync from API if user has a selection
    // 2. After submission: sync from API to reflect the submitted selection
    if (!hasInitializedFromApi.current) {
      // Initial load - sync from API
      if (userSelectedSquareId !== null) {
        setSelectedSquares([userSelectedSquareId]);
        hasInitializedFromApi.current = true;
      } else if (userSelectedSquareNumber === null) {
        // Explicitly null means no selection
        setSelectedSquares([]);
        hasInitializedFromApi.current = true;
      }
    } else if (lastSubmittedSquare.current !== null) {
      // After submission, sync with API to show the submitted selection
      if (userSelectedSquareId !== null) {
        setSelectedSquares([userSelectedSquareId]);
        lastSubmittedSquare.current = null; // Reset after sync
      }
    }
  }, [selectionData, isResultsMode]);

  // Handle loading state
  const isLoading = contestLoading || (isResultsMode && selectionLoading);
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#53d337]" />
          <p className="text-zinc-400">
            {isResultsMode && selectionLoading ? 'Loading your selection...' : 'Loading contest...'}
          </p>
        </div>
      </div>
    );
  }

  // Handle no contest / no pending contests — show friendly empty state
  const noContestMessage =
    contestError instanceof Error ? contestError.message : !contest ? 'Contest not found' : '';
  const isNoContestsAvailable =
    !contest &&
    (noContestMessage.toLowerCase().includes('pending') ||
      noContestMessage.toLowerCase().includes('no available') ||
      noContestMessage.toLowerCase().includes('not found') ||
      noContestMessage === '');

  if (contestError || !contest) {
    if (isNoContestsAvailable) {
      return (
        <div className="px-4 py-4 flex items-center justify-center min-h-[300px]">
          <div className="bg-zinc-800/50 border border-zinc-600/50 rounded-lg p-6 text-center max-w-md">
            <p className="text-zinc-100 font-semibold text-lg">No open contests available</p>
            <p className="text-zinc-400 mt-2">Check back later for new contests!</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 text-[#53d337] hover:text-[#6ee04a] font-medium underline"
            >
              Go to contests
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="px-4 py-4">
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-400 font-semibold">Error loading contest</p>
          <p className="text-red-300 text-sm mt-1">
            {contestError instanceof Error ? contestError.message : 'Contest not found'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-red-400 hover:text-red-300 underline"
          >
            Go back to contests
          </button>
        </div>
      </div>
    );
  }

  // Handle missing contestId
  if (!contestId) {
    return (
      <div className="px-4 py-4">
        <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
          <p className="text-yellow-400 font-semibold">No contest selected</p>
          <p className="text-yellow-300 text-sm mt-1">Please select a contest to play.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-yellow-400 hover:text-yellow-300 underline"
          >
            Go back to contests
          </button>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return null;
  }

  const handleSquareClick = (squareId: number) => {
    if (isResultsMode) {
      // In results mode, clicking on selected square shows results modal
      if (selectedSquares.includes(squareId)) {
        setShowResultsModal(true);
      }
      return;
    }
    setSelectedSquares(prev => {
      // If already selected, deselect
      if (prev.includes(squareId)) {
        return prev.filter(id => id !== squareId);
      }
      // If we can only select one, replace
      if (gameData?.selectableSquares === 1) {
        return [squareId];
      }
      // Otherwise add to selection (up to limit)
      if (prev.length < (gameData?.selectableSquares || 1)) {
        return [...prev, squareId];
      }
      return prev;
    });
  };

  const handleInfoClick = () => {
    setShowInfoPopup(true);
  };

  const handleSubmitEntry = async () => {
    if (!contest || !questionId || selectedSquares.length === 0) {
      return;
    }

    // Get pickem ID from contest
    const pickemId = contest.pickem_id;
    if (!pickemId) {
      console.error('No pickem_id found in contest');
      return;
    }

    // Map square numbers to option IDs
    if (optionsLoading) {
      console.error('Options data is still loading, please wait...', {
        questionId,
        optionsLoading,
      });
      return;
    }

    if (optionsError) {
      console.error('Error loading options data:', optionsError);
      return;
    }

    if (!typedOptionsData?.square_to_option_map) {
      console.error('Options data not loaded yet.', {
        questionId,
        hasOptionsData: !!optionsData,
        hasTypedOptionsData: !!typedOptionsData,
        squareToOptionMap: typedOptionsData?.square_to_option_map,
      });
      return;
    }

    const selections = selectedSquares
      .map(squareId => {
        // Convert square.id (0-99) to square_number (1-100)
        const squareNumber = squareId + 1;
        const optionId = typedOptionsData.square_to_option_map[squareNumber];
        if (!optionId) {
          console.error(`No option ID found for square ${squareNumber}`);
          return null;
        }
        return {
          question_id: questionId,
          option_id: optionId,
        };
      })
      .filter(Boolean) as Array<{ question_id: string; option_id: string }>;

    if (selections.length === 0) {
      console.error('No valid selections to submit');
      return;
    }

    try {
      await submitMutation.mutateAsync({
        pickemId,
        selections,
      });
      // Optimistically add contest to My Games so the tile appears immediately
      if (contest) {
        upsertContestInMyPicksCache(queryClient, contest, selectedSquares[0]);
      }
      // Track the submitted square (store square.id for internal tracking)
      lastSubmittedSquare.current = selectedSquares[0] !== undefined ? selectedSquares[0] : null;
      // Refetch selection to update UI with the new selection
      await refetchSelection();
      // Show modal after successful submission
      setShowSubmittedModal(true);
    } catch (error) {
      console.error('Failed to submit selection:', error);
      // Error toast is handled by the mutation
    }
  };

  return (
    /*
     * `min-h-full` (not `h-full`) fills the bounded <main> on short content, and
     * still lets this box grow when the square board is taller than the viewport
     * so <main> scrolls rather than crushing the cells.
     */
    <div className="flex flex-col min-h-full bg-[#121212]">
      {/* Game Header with Jerseys and Prize */}
      <GameHeader
        contestId={contestId}
        team1={gameData.team1}
        team2={gameData.team2}
        prizePool={gameData.prizePool}
        contestName={gameData.contestName}
        selectableSquares={gameData.selectableSquares}
        onInfoClick={handleInfoClick}
        isResultsMode={isResultsMode}
        selectedSquare={gameData.userSelectedSquare}
        rowNumbers={gameData.rowNumbers}
        colNumbers={gameData.colNumbers}
        team1Score={gameData.team1Score}
        team2Score={gameData.team2Score}
        sponsorLogoUrl={contest.badge_image_url}
        backgroundImageUrl={contest.background_image_url}
        backgroundImageMobileUrl={contest.background_image_mobile_url}
        startTimeUtc={contest.start_time_utc}
        isGameLive={gameData.isGameLive}
        liveFixtureData={liveFixtureData}
        sport={contest.sport}
      />

      {/* Reserve a Seat (DKPLAT-529): while the squares aren't posted yet
          (now < questions_posted_at), show the reserve CTA instead of the grid.
          The grid takes over once questions post. */}
      <div className="w-full shrink-0 md:flex md:grow md:basis-0 md:flex-col">
        {showReservePanel ? (
          <ReservePanel
            id={contestId || ''}
            contestName={gameData.contestName}
            prizePool={gameData.prizePool}
            questionsPostedAt={contest.questions_posted_at ?? null}
            hasReservation={hasReservation}
            isSubmitting={reserveMutation.isPending}
            onReserve={handleReserve}
          />
        ) : (
          <>
            {/*
             * Squares Grid.
             *
             * Width-keyed and `shrink-0` at every breakpoint: cells stay `aspect-square`
             * (this is a squares game), so height follows width. Stretching the grid into
             * leftover height is what turned desktop cells into letterbox rectangles.
             *
             * If the square board is taller than the leftover viewport, PlayView's
             * `min-h-full` box grows and <main> scrolls — better than crushed rows.
             * Extra leftover on a tall screen sits below the CTA, above the footer ad.
             */}
            <div className="w-full shrink-0">
              <SquaresGrid
                team1={{
                  name: gameData.team1.name,
                  primaryColor: gameData.team1.primaryColor,
                  textColor: '#ffffff',
                }}
                team2={{
                  name: gameData.team2.name,
                  primaryColor: gameData.team2.primaryColor,
                  textColor: '#ffffff',
                }}
                squares={gameData.squares}
                selectedSquares={selectedSquares}
                onSquareClick={handleSquareClick}
                isLocked={gameData.isLocked}
                numbersAssigned={gameData.numbersAssigned}
                rowNumbers={gameData.rowNumbers}
                colNumbers={gameData.colNumbers}
                showResults={effectiveResultsMode}
                isGameLive={gameData.isGameLive}
                winningSquaresByPeriod={winningSquaresData?.winningSquaresByPeriod || {}}
                periods={Array.isArray(contest?.periods) ? contest.periods : undefined}
                sport={contest.sport}
                optionsData={(() => {
                  // Build map of square number to option data for quick lookup
                  if (!typedOptionsData?.options) return undefined;
                  const map = new Map<
                    number,
                    { square_number: number; entry_count: number; win_probability: number }
                  >();
                  typedOptionsData.options.forEach(opt => {
                    if (opt.square_number !== null) {
                      map.set(opt.square_number, {
                        square_number: opt.square_number,
                        entry_count: opt.entry_count,
                        win_probability: opt.win_probability,
                      });
                    }
                  });
                  return map;
                })()}
              />
            </div>

            {/* Submit / Edit Entry Button - only show when entries are NOT locked */}
            {!effectiveResultsMode && (
              <div className={`px-4 pt-2 pb-4 ${MATCHUP_BAND}`}>
                <button
                  onClick={handleSubmitEntry}
                  disabled={
                    selectedSquares.length === 0 ||
                    optionsLoading ||
                    !typedOptionsData?.square_to_option_map
                  }
                  className="w-full rounded-lg flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: '#53D337',
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 800,
                    fontSize: '21.85px',
                    lineHeight: '21.85px',
                    letterSpacing: '0.5%',
                    color: '#000000',
                    padding: '12px 24px',
                  }}
                >
                  <span>
                    {selectionData?.selection?.square_number != null
                      ? 'EDIT ENTRY'
                      : 'SUBMIT ENTRY'}
                  </span>
                </button>
              </div>
            )}
          </>
        )}

        {/* See My Results Button - when entries locked (ACTIVE/COMPLETE) or explicit results mode */}
        {effectiveResultsMode && (
          <div className={`px-4 pt-2 pb-4 ${MATCHUP_BAND}`}>
            <button
              onClick={() => setShowResultsModal(true)}
              className="w-full rounded-lg flex items-center justify-center gap-2 transition-opacity hover:opacity-90 active:opacity-80"
              style={{
                backgroundColor: '#53D337',
                fontFamily: '"Saira Extra Condensed", sans-serif',
                fontWeight: 800,
                fontSize: '21.85px',
                lineHeight: '21.85px',
                letterSpacing: '0.5%',
                color: '#000000',
                padding: '12px 24px',
              }}
            >
              <span>SEE MY RESULTS</span>
            </button>
          </div>
        )}
      </div>

      {/*
       * Slot the anchored banner sits over. `shrink-0` is load-bearing: this is a
       * flex-column child, so without it the spacer is the first thing squeezed on a
       * short viewport and the Submit Entry CTA ends up unreachable behind the ad.
       * The height tracks the banner's measured height plus the DK tab bar, so the
       * last grid row and the CTA can always scroll fully clear of both.
       */}
      <div style={{ height: FOOTER_ADVERT_RESERVED_SPACE }} className="shrink-0" aria-hidden />

      {/* Footer Advert — anchored above the DK tab bar, does not move on scroll. */}
      <FooterAdvert
        contestId={contestId}
        contestStickyFooterPlacement={contest?.sticky_footer_placement}
      />

      {/* Reserve a Seat — confirm then confirmation (DKPLAT-529) */}
      <ConfirmReserveModal
        isOpen={showConfirmReserveModal}
        onClose={() => setShowConfirmReserveModal(false)}
        onConfirm={handleConfirmReserve}
        contestName={gameData.contestName}
        prizePool={gameData.prizePool}
        isSubmitting={reserveMutation.isPending}
      />

      <SeatReservedModal
        isOpen={showSeatReservedModal}
        onClose={() => setShowSeatReservedModal(false)}
        reservationId={reservationId}
        questionsPostedAt={contest.questions_posted_at ?? null}
        onGoToMyGames={() => {
          setShowSeatReservedModal(false);
          navigate(appendLobbyParams('/my-picks', searchParams));
        }}
        onBackToLobby={() => {
          setShowSeatReservedModal(false);
          if (isFromLobby(searchParams)) {
            navigateToLobby(searchParams);
            return;
          }
          navigate('/');
        }}
      />

      {/* Square Submitted Modal */}
      <SquareSubmittedModal
        isOpen={showSubmittedModal}
        onClose={() => setShowSubmittedModal(false)}
        team1Name={gameData.team1.abbreviation}
        team1JerseyUrl={gameData.team1.jerseyUrl}
        team2Name={gameData.team2.abbreviation}
        team2JerseyUrl={gameData.team2.jerseyUrl}
        gameDate={
          contest.start_time_utc
            ? new Date(contest.start_time_utc).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })
            : ''
        }
        gameTime={
          contest.start_time_utc
            ? new Date(contest.start_time_utc).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : ''
        }
        sponsorLogoUrl={contest.badge_image_url}
        promoImageUrl={contest.submit_view_image_url}
        promoImageMobileUrl={contest.submit_view_image_mobile_url}
        contestId={contestId}
      />

      {/* Square Results Modal */}
      {gameData &&
        (() => {
          // Calculate user statistics from options data
          const totalUsers = typedOptionsData?.total_entries || 0;
          const userSquareNumber = selectionData?.selection?.square_number;
          const userEntryCount =
            userSquareNumber && typedOptionsData
              ? typedOptionsData.options.find(opt => opt.square_number === userSquareNumber)
                  ?.entry_count || 0
              : 0;
          const userPercentage = totalUsers > 0 ? (userEntryCount / totalUsers) * 100 : 0;

          // Format prize amounts from selection data
          // The backend returns prize_amounts_by_period as Record<string, string>
          const prizeAmountsByPeriod = selectionData?.selection?.prize_amounts_by_period || {};

          // Get period codes from contest periods or use defaults
          const periods = contest?.periods || [];
          const periodCodes =
            periods.length > 0
              ? periods.map((p: any) => p.periodCode || p.period_code || 'Q1')
              : ['Q1', 'Q2', 'Q3', 'FINAL']; // Default periods

          // Build map of periodCode -> graded scores from winning squares
          const periodsWithScores = winningSquaresData?.periods || [];
          const periodScoresMap = new Map<string, { rowTeamScore: number; colTeamScore: number }>();
          for (const p of periodsWithScores) {
            const code = p.periodCode || (p as any).period_code;
            if (code != null) {
              periodScoresMap.set(code, {
                rowTeamScore: p.rowTeamScore ?? 0,
                colTeamScore: p.colTeamScore ?? 0,
              });
            }
          }

          // Format prize amounts for modal (convert to PotentialWinning format)
          const potentialWinnings = periodCodes.map((periodCode: string) => {
            // Try multiple possible keys (exact match, uppercase, lowercase)
            // Also try alternative formats (Q1 vs 1Q, FINAL vs FT)
            let amount =
              prizeAmountsByPeriod[periodCode] ||
              prizeAmountsByPeriod[periodCode.toUpperCase()] ||
              prizeAmountsByPeriod[periodCode.toLowerCase()];

            // If still not found, try alternative period code formats
            if (!amount) {
              if (periodCode === 'FT' || periodCode === 'FINAL') {
                amount =
                  prizeAmountsByPeriod['FINAL'] ||
                  prizeAmountsByPeriod['FT'] ||
                  prizeAmountsByPeriod['final'];
              } else if (periodCode.startsWith('Q')) {
                // Try Q1, 1Q, Q1, etc.
                const qNum = periodCode.replace('Q', '');
                amount = prizeAmountsByPeriod[`Q${qNum}`] || prizeAmountsByPeriod[`${qNum}Q`];
              }
            }

            const formattedAmount = amount ? `$${parseFloat(String(amount)).toFixed(2)}` : '$0.00';

            // Format period code for display (Q1, Q2, Q3, FT)
            const displayCode = periodCode === 'FINAL' ? 'FT' : periodCode;

            // Get graded scores for this period (row=team1, col=team2)
            const scores = periodScoresMap.get(periodCode);

            return {
              quarter: displayCode,
              amount: formattedAmount,
              team1Score: scores?.rowTeamScore,
              team2Score: scores?.colTeamScore,
            };
          });

          // Check if game is completed
          const contestStatus = contest?.status?.toUpperCase();
          const isCompleted = contestStatus === 'COMPLETE' || contest?.status === 'final';

          return (
            <SquareResultsModal
              contestId={contestId}
              isOpen={showResultsModal}
              onClose={() => setShowResultsModal(false)}
              team1Name={gameData.team1.abbreviation}
              team1JerseyUrl={gameData.team1.jerseyUrl}
              team2Name={gameData.team2.abbreviation}
              team2JerseyUrl={gameData.team2.jerseyUrl}
              team1Score={gameData.team1Score}
              team2Score={gameData.team2Score}
              sponsorLogoUrl={contest.badge_image_url}
              userCount={userEntryCount}
              userPercentage={userPercentage}
              totalUsers={totalUsers}
              potentialWinnings={potentialWinnings}
              isCompleted={isCompleted}
              userSquareRowDigit={
                selectionData?.selection?.square_number &&
                gameData.rowNumbers &&
                gameData.rowNumbers.length === 10
                  ? gameData.rowNumbers[
                      Math.floor((selectionData.selection.square_number - 1) / 10)
                    ]
                  : undefined
              }
              userSquareColDigit={
                selectionData?.selection?.square_number &&
                gameData.colNumbers &&
                gameData.colNumbers.length === 10
                  ? gameData.colNumbers[(selectionData.selection.square_number - 1) % 10]
                  : undefined
              }
              sport={contest.sport}
            />
          );
        })()}

      {/* Info popup - RulesView slide-up (contest details/how to play) */}
      {contestId && showInfoPopup && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setShowInfoPopup(false)}
            aria-hidden
          />
          <div
            className={`fixed inset-x-0 z-50 flex flex-col overflow-hidden bg-[#121212] rounded-t-2xl shadow-2xl ${DESKTOP_COLUMN_FIXED} rules-popup-enter`}
            style={RULES_POPUP_PANEL_STYLE}
          >
            <RulesView
              contestId={contestId}
              variant="popup"
              onClose={() => setShowInfoPopup(false)}
              onPlayNow={() => {
                setShowInfoPopup(false);
                // Already on PlayView with this contest
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
